import React, { useCallback, useEffect, useState, useRef } from 'react';

import { Box } from '@mui/material';
import { useAuthContext } from 'AuthProvider';
import { MasterSearchFrom, FileDownloadData, SearchBox } from 'components/commonparts';
import { StockCenterItemAlertDialog } from 'components/dialog';
import { SearchCondition, DataList } from 'components/parts/MasterStockCenterItem';
import { useCenter } from 'hooks/useCenter';
import { useCenterStockItem } from 'hooks/useCenterStockItem';
import { useGridSearch } from 'hooks/useGridSearch';
import { useItem } from 'hooks/useItems';
import { useOrderAlert } from 'hooks/useOrderAlert';
import { useSupplier } from 'hooks/useSupplier';
import { useTypecode } from 'hooks/useTypecode';
import { useSearchParamStore } from 'store/searchParamStore';
import { getLocalSearchParam } from 'utils/getLocalSearchParam';

import type { GetMstCenterStockItemCenterStockItemGetRequest, MstCenterStockItem, ResponseResult, SearchTargetEnabled } from 'api-client';
import type { StockCenterItemAlertDialogEdit } from 'components/dialog/StockCenterItemAlertDialog/StockCenterItemAlertDialog';

function MasterStockCenterItem() {
  const { loginUserInfo, typeCode: typeCodes } = useAuthContext()
  const trCompanyId = loginUserInfo?.companyId;
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)

  const localCenterId = localSearchParam?.centerStockItem.centerId || undefined
  const localOrgItemCode = localSearchParam?.centerStockItem.orgItemCode || undefined
  const localSearchTargetEnabled = localSearchParam?.centerStockItem.searchTargetEnabled || undefined
  const defaultSearchTargetEnabled: SearchTargetEnabled | undefined =
  typeCodes.find((group) => group.typeGroup === 'search_target_enabled')?.codes.find((code) => code.defaultFlg)?.typeCode as SearchTargetEnabled | undefined 

  // 自社のcompanyIdのみ検索条件をローカルストレージから復元
  const initSearchParam: GetMstCenterStockItemCenterStockItemGetRequest = {
    companyId: trCompanyId ?? loginUserInfo!.companyId,
    centerId: localCenterId,
    orgItemCode: localOrgItemCode,
    searchTargetEnabled: localSearchTargetEnabled ?? defaultSearchTargetEnabled,
  }

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [downloadGridRows, setDownloadGridRows] = useState<MstCenterStockItem[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([]);

  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)

  const { fetchCenterStockItemSearch, centerstockitemSearchIsLoading, centerstockitemSearch, centerstockitemSearchResult } = useCenterStockItem()
  const { fetchCenterSearch, centerSearch } = useCenter()
  const { fetchItems, items } = useItem()
  const { fetchAlertSummary } = useOrderAlert()
  const { fetchSupplierSearch, supplierSearch } = useSupplier()
  const { fetchTypeCode, typeCode } = useTypecode()
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })
  const [searchParam, setSearchParam] = useState<GetMstCenterStockItemCenterStockItemGetRequest>(initSearchParam)
  const isFirstRender = useRef(true);
  const [uploadData, setUploadData] = useState<any[]>([])
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  const [isAlert, setIsAlert] = useState<boolean | null>(null)
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false)
  const [alertDialogResetSignal, setAlertDialogResetSignal] = useState(0)
  const [alertDialogEdits, setAlertDialogEdits] = useState<StockCenterItemAlertDialogEdit[]>([])
  const [alertDialogFocusTarget, setAlertDialogFocusTarget] = useState<{ orgCenterId: string; orgItemCode: string } | undefined>(undefined)


  useEffect(() => {
    setResultMessage(centerstockitemSearchResult)
  }, [centerstockitemSearchResult])

  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage, gridMessage?.status, gridMessage?.message])

  useEffect(() => {
    setSearchParam(searchParam)
  }, [searchParam])

  const doSearch = useCallback(async (requestParam: GetMstCenterStockItemCenterStockItemGetRequest) => {
    setAlertDialogEdits([])
    setAlertDialogResetSignal((prev) => prev + 1)
    setSearchParam(requestParam)
    try {
      const [, alertSummaryResponse] = await Promise.all([
          fetchCenterStockItemSearch({
            enabled: false,
            ...requestParam,
          }),
          fetchAlertSummary({
            enabled: false,
            centerId: requestParam.centerId || undefined,
          }, true),
        ])
        setIsAlert(alertSummaryResponse?.data?.isMstCenterStockItemAlert ?? null)
        if (requestParam.companyId === loginUserInfo!.companyId && loginUserInfo?.tenantId && loginUserInfo?.companyId) {
          setSearchParamState<GetMstCenterStockItemCenterStockItemGetRequest>(
            loginUserInfo?.tenantId,
            loginUserInfo?.companyId,
            'CenterStockItem',
            { 
              ...requestParam,
            }
          );
        }
  
        setHasGridEdited(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error);
      } 
  }, [
    hasGridEdited,
      setDiscardChangeDialogOpen,
      fetchCenterStockItemSearch,
      loginUserInfo,
      setSearchParamState,
      setSearchParam,
      setHasGridEdited,
      fetchAlertSummary
  ]);

  const handleRegister = () => {
    setAlertDialogEdits([])
    setAlertDialogResetSignal((prev) => prev + 1)
    setUpdateTrigger((prev) => !prev);
  }

  const handleUploadData = async (headers: any[], data: any[]) => {
    // エクセルファイルからデータをインポートし、ヘッダーとデータ行に分けて渡される
    const mappedData = data.map((row) => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
    setUploadData(mappedData); 
  };

  const onReload = () => {
    // 検索処理の実行を行うトリガー
    setAlertDialogEdits([])
    setAlertDialogResetSignal((prev) => prev + 1)
    setSearchTrigger((prev) => !prev);
  }

  useEffect(() => {
    const firstTimeRender = async () => { 
      if (isFirstRender.current) {
        isFirstRender.current = false
        const itemsCenterStockPromise = fetchCenterStockItemSearch({
          enabled: false,
          ...searchParam,
        });
        const alertSummaryPromise = fetchAlertSummary({
          enabled: false,
          centerId: searchParam.centerId || undefined,
        }, true);
        const itemsPromise = fetchItems({
          enabled: false,
          companyId: searchParam.companyId, 
        });
        const centerPromise = fetchCenterSearch({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        const supplierPromise = fetchSupplierSearch({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        const typePromise = fetchTypeCode();
        const [, alertSummaryResponse] = await Promise.all([
          itemsCenterStockPromise,
          alertSummaryPromise,
          itemsPromise,
          centerPromise,
          supplierPromise,
          typePromise
        ]);
        setIsAlert(alertSummaryResponse?.data?.isMstCenterStockItemAlert ?? null)
      }
    }
    firstTimeRender()
  }, [])


  const generateDownloadData = (): MstCenterStockItem[] => {
    if (!centerstockitemSearch?.list || centerstockitemSearch.list.length === 0) {
      return [{} as MstCenterStockItem]
    }
    const downloadData: MstCenterStockItem[] = [...centerstockitemSearch.list]
    return downloadData
  }

  const handleFileDownload = () => {
    setDownloadGridRows(generateDownloadData())
  }

  return (
    <Box>
      <MasterSearchFrom
        result={resultMessage}
        onDoUpdate={handleRegister}
        onReload={onReload}
        onImportData={handleUploadData}
        onFileDownLoad={handleFileDownload}
        discardChangeDialogOpen={discardChangeDialogOpen}
        setDownloadMode={setDownloadMode}
        hasDataEdited={hasGridEdited}
        importRequiredFields={["org_center_id","org_item_code"]}
        headerJp={["自社センター","商品コード"]}
        lastUpdateDatetime={centerstockitemSearch?.lastUpdateDatetime}
      >
        <SearchCondition
            hasDataEdited={hasGridEdited}
            searchParam={initSearchParam}
            onSearch={doSearch}
            setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
            searchTrigger={searchTrigger}
            isAlert={isAlert}
            onAlertClick={() => setIsAlertDialogOpen(true)}
        />
      </MasterSearchFrom>
      <StockCenterItemAlertDialog
        open={isAlertDialogOpen}
        onClose={() => setIsAlertDialogOpen(false)}
        onApplyEdits={(edits: StockCenterItemAlertDialogEdit[]) => setAlertDialogEdits(edits)}
        onNavigateToRecord={(target: { orgCenterId: string; orgItemCode: string }) => {
          setAlertDialogFocusTarget(target)
          setIsAlertDialogOpen(false)
        }}
        resetSignal={alertDialogResetSignal}
        searchParam={searchParam}
        center={centerSearch.list}
        items={items.list}
      />
      <FileDownloadData fileName="センター在庫管理" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />
      <SearchBox
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onHide={gridSearch.searchBoxProps.onHide}
      />
      <DataList
          list={centerstockitemSearch}
          items={items.list}
          center={centerSearch.list}
          supplier={supplierSearch.list}
          systype={typeCode}
          onReload={onReload}
          isLoading={centerstockitemSearchIsLoading}
          searchParam={searchParam}
          updateTrigger={updateTrigger}
          onUploadData={uploadData}
          setHasGridEdited={setHasGridEdited}
          setResultMessage={setGridMessage}
          setDownloadColumnDefs={setDownloadColDef}
          dialogEdits={alertDialogEdits}
          focusTarget={alertDialogFocusTarget}
          onFocusHandled={() => setAlertDialogFocusTarget(undefined)}
          gridRef={gridSearch.gridRef}
          findSearchValue={gridSearch.gridSearchProps.findSearchValue}
          onFindChanged={gridSearch.gridSearchProps.onFindChanged}
          isSearchVisible={gridSearch.searchBoxProps.isVisible}
        />
    </Box>
          
  )
}

export default MasterStockCenterItem;
